import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import HomePage from "@/components/pages/HomePage";
import ServicePage from "@/components/pages/ServicePage";
import SpecialServicePage from "@/components/pages/SpecialServicePage";
import CityServicePage from "@/components/pages/CityServicePage";
import AboutPage from "@/components/pages/AboutPage";
import ContactPage from "@/components/pages/ContactPage";
import NotFoundPage from "@/components/pages/NotFoundPage";
import AdminLoginPage from "@/components/pages/AdminLoginPage";
import AdminDashboardPage from "@/components/pages/AdminDashboardPage";
import AdminSeoPage from "@/components/pages/AdminSeoPage";
import AdminSeoCallbackPage from "@/components/pages/AdminSeoCallbackPage";

import { CITY_DATA, CITY_PAGES } from "@/data/siteContent";

const NotFoundWrapper = () => {
  const location = useLocation();
  const language = location.pathname.startsWith("/en") ? "en" : "fr";
  return <NotFoundPage language={language} />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />

        <Toaster position="top-right" richColors data-testid="global-toaster" />

        <Routes>
          <Route path="/" element={<Navigate to="/fr" replace />} />
          <Route path="/fr" element={<HomePage language="fr" />} />
          <Route path="/en" element={<HomePage language="en" />} />

          <Route path="/fr/services/reparation-pave-uni" element={<ServicePage language="fr" serviceKey="repair" />} />
          <Route path="/fr/services/installation-pave-uni" element={<ServicePage language="fr" serviceKey="installation" />} />
          <Route path="/fr/services/nettoyage-pave-uni" element={<SpecialServicePage language="fr" serviceKey="cleaning" />} />
          <Route path="/fr/services/scellant-protecteur-pave-uni" element={<SpecialServicePage language="fr" serviceKey="sealing" />} />

          <Route path="/en/services/paver-cleaning" element={<SpecialServicePage language="en" serviceKey="cleaning" />} />
          <Route path="/en/services/paver-sealing" element={<SpecialServicePage language="en" serviceKey="sealing" />} />
          <Route path="/en/services/paver-repair" element={<ServicePage language="en" serviceKey="repair" />} />
          <Route path="/en/services/paver-installation" element={<ServicePage language="en" serviceKey="installation" />} />

          {CITY_PAGES.map((p) => (
            <Route
              key={p.slug}
              path={p.path}
              element={<CityServicePage language="fr" serviceKey={p.serviceKey || "cleaning"} city={p.city} />}
            />
          ))}

          {CITY_DATA.flatMap((city) => [
            <Route key={`${city.slug}-repair-fr`} path={`/fr/villes/${city.slug}/reparation-pave-uni`} element={<CityServicePage language="fr" city={city} serviceKey="repair" />} />,
            <Route key={`${city.slug}-installation-fr`} path={`/fr/villes/${city.slug}/installation-pave-uni`} element={<CityServicePage language="fr" city={city} serviceKey="installation" />} />,
            <Route key={`${city.slug}-cleaning-fr`} path={`/fr/villes/${city.slug}/nettoyage-pave-uni`} element={<CityServicePage language="fr" city={city} serviceKey="cleaning" />} />,
            <Route key={`${city.slug}-sealing-fr`} path={`/fr/villes/${city.slug}/scellant-protecteur-pave-uni`} element={<CityServicePage language="fr" city={city} serviceKey="sealing" />} />,

            <Route key={`${city.slug}-repair-en`} path={`/en/cities/${city.slug}/paver-repair`} element={<CityServicePage language="en" city={city} serviceKey="repair" />} />,
            <Route key={`${city.slug}-installation-en`} path={`/en/cities/${city.slug}/paver-installation`} element={<CityServicePage language="en" city={city} serviceKey="installation" />} />,
          ])}

          <Route path="/fr/a-propos" element={<AboutPage language="fr" />} />
          <Route path="/en/about" element={<AboutPage language="en" />} />
          <Route path="/fr/contact" element={<ContactPage language="fr" />} />
          <Route path="/en/contact" element={<ContactPage language="en" />} />

          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/seo" element={<AdminSeoPage />} />
          <Route path="/admin/seo/callback" element={<AdminSeoCallbackPage />} />

          <Route path="/chambly/*" element={<Navigate to="/fr/villes/chambly/reparation-pave-uni" replace />} />
          <Route path="/saint-lambert/*" element={<Navigate to="/fr/villes/saint-lambert/reparation-pave-uni" replace />} />
          <Route path="/pave-uni-boucherville/*" element={<Navigate to="/fr/villes/boucherville/reparation-pave-uni" replace />} />
          <Route path="/pave-uni-longueuil/*" element={<Navigate to="/fr/villes/longueuil/reparation-pave-uni" replace />} />
          <Route path="/saint-jean-sur-richelieu/*" element={<Navigate to="/fr/villes/saint-jean-sur-richelieu/reparation-pave-uni" replace />} />
          <Route path="/reparation-de-pave-uni/*" element={<Navigate to="/fr/services/reparation-pave-uni" replace />} />
          <Route path="/longueuil/*" element={<Navigate to="/fr/villes/longueuil/reparation-pave-uni" replace />} />
          <Route path="/pave-uni-rive-sud-1/*" element={<Navigate to="/fr/services/reparation-pave-uni" replace />} />

          <Route path="*" element={<NotFoundWrapper />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;